import * as Dialog from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AppleIcon, SpotifyIcon, Wallet03Icon } from "hugeicons-react";
import { X, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { DAYJS_FORMAT } from "../Component/Index/Events/EventCard";
import EventBanner from "../Component/Index/Events/EventBanner";
import VerifyUserInfo from "../Component/shared/VerifyUserInfo";
import { CalenderIcon, LocationIcon } from "../Component/utils/icon";
import { eventDetails, getTribes } from "../api/api";
import { JoinTribeCard } from "../modals/tribes/JoinTribeCard";
import {
  PostOnboardingAction,
  clearPostOnboardingAction,
  getPostOnboardingAction,
} from "../utils/postOnboardingAction";
import { EventTicket, EventDetailsResponse } from "../types/event.types";
import { Tribe } from "../types/tribes.types";
import { useJoinTribe } from "../hooks/useJoinTribe";
import { useAvailableTribe } from "../hooks/useAvailableTribe";
import { numberToTribeName } from "../utils/tribeUtils";
import { Button } from "../Component/shared/Button";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
interface TribesResponse {
  data: {
    docs: Tribe[];
  };
}

const overlayStyles = {
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  position: "fixed",
  inset: 0,
  animation: "overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1)",
} as const;

const contentStyles = {
  backgroundColor: "hsl(var(--background))",
  borderRadius: "6px",
  boxShadow:
    "hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px",
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: "600px",
  maxHeight: "85vh",
  padding: "25px",
  animation: "contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1)",
  border: "1px solid hsl(var(--border))",
} as const;

const Event = () => {
  const { identifier } = useParams<{ identifier: string }>();
  const { user, preferences } = useSelector((state: any) => state.user);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get("from");
  const action = searchParams.get("action") as PostOnboardingAction;
  const [isRedirectingModalOpen, setIsRedirectingModalOpen] = useState(false);
  const navigate = useNavigate();
  const { data: eventData, isLoading: isEventLoading } = useQuery({
    queryKey: ["event", identifier],
    queryFn: async () => {
      const response = (await eventDetails(
        identifier!
      )) as EventDetailsResponse;
      return response.data.data;
    },
    enabled: !!identifier,
  });

  const { data: tribeData } = useQuery({
    queryKey: ["tribeCounts", identifier],
    queryFn: async () => {
      const requestData = {
        event_id: identifier,
        search_query: "",
        page: 1,
        limit: 10,
        sortBy: "createdAt",
        order: -1,
      };

      const response = (await getTribes(requestData)) as TribesResponse;

      return response.data;
    },
    enabled: !!identifier,
  });

  const { data: availableTribe } = useAvailableTribe(eventData?._id ?? "");

  const { mutate: joinTribeMutation, isPending } = useJoinTribe();

  useEffect(() => {
    if (from === "onboarding" || from === "auth") {
      const action = getPostOnboardingAction();

      const clearActions = () => {
        setSearchParams((prev) => {
          prev.delete("from");
          prev.delete("action");
          clearPostOnboardingAction();
          return prev;
        });
      };

      switch (action?.action) {
        case "join_tribe":
          setIsJoinModalOpen(true);
          clearActions();
          break;
        case "get_ticket":
          const ticketUrl = action?.ticketUrl;
          if (ticketUrl) {
            setIsRedirectingModalOpen(true);
            setTimeout(() => {
              window.open(ticketUrl, "_blank");
              setIsRedirectingModalOpen(false);
              clearActions();
            }, 2000);
          }
          break;
        case "view_event":
          clearActions();
          break;
        default:
          break;
      }
    }
  }, [action, from, searchParams, setSearchParams]);

  const getEventType = (type?: string) => {
    const types = {
      festival: "Music Festival",
      concert: "Music Concert",
      sports: "Sports Event",
    };
    return types[type as keyof typeof types] || "";
  };

  const splitTextIntoSections = (text: string) => {
    if (!text) return [];

    const cleanText = text.replace(/https?:\/\/\S+/g, "").trim();
    const paragraphs = cleanText.split(/(?<=\.)(?:\s+|\n+)(?=[A-Z])/);

    return paragraphs
      .filter((paragraph) => paragraph.trim().length > 0)
      .map((paragraph, index) => (
        <div key={index} className="py-2 my-2">
          {paragraph.trim()}
        </div>
      ));
  };

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  if (isEventLoading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        Loading...
      </div>
    );
  if (!eventData)
    return (
      <div className="flex items-center justify-center min-h-screen">
        Event not found
      </div>
    );

  const eventType = getEventType(eventData.event_type);
  const startDate = dayjs(eventData.event_start_date).format(DAYJS_FORMAT);
  const endDate = dayjs(
    eventData.event_end_date || eventData.event_start_date
  ).format(DAYJS_FORMAT);
  const metaDescription = `Get access to face-value tickets, Connect with fellow festival-goers, and Experience Lollapalooza Together. Join the ${eventData.event_name} ${eventType} tribe now!`;

  return (
    <>
      <Helmet>
        <meta name="description" content={metaDescription} />
        <title>
          Experience Live Music Together - Join the {eventData.event_name}{" "}
          {eventType} Tribe!
        </title>
      </Helmet>

      <div className="max-w-screen-lg mx-auto py-10 container">
        <Button
          onClick={() => navigate("/Myevents")}
          variant="outline"
          size="xs"
          className="mb-2 group"
        >
          <ArrowLeft className="w-4 h-4 mr-2  group-hover:text-utility-pink transition-colors" />
          Back to My Events
        </Button>
        <EventBanner
          tribes={tribeData?.docs ?? []}
          bannerImage={eventData?.banner_image ?? ""}
        />

        <div className="mt-6 pb-40">
          <div>
            <h3 className="text-primary-100 text-2xl font-medium tracking-tight">
              {eventData.event_name}・{eventType}
            </h3>

            <div className="coachella_music_date_flx">
              <div className="coachella_music_date">
                <CalenderIcon />
                <p className="text-primary-400 text-sm">
                  {startDate === endDate
                    ? startDate
                    : `${startDate} - ${endDate}`}
                </p>
              </div>

              <div className="coachella_music_date">
                <LocationIcon />
                <p className="text-primary-400 text-sm">
                  {eventData.event_venue?.name} •{" "}
                  {eventData.event_venue?.zip_code},{" "}
                  {eventData.event_venue?.country}
                </p>
              </div>

              <div>
                {eventData.event_tickets?.length ? (
                  eventData.event_tickets.map((ticket: EventTicket) => (
                    <div key={ticket._id} className="flex items-center gap-2">
                      <Wallet03Icon className="w-4 text-primary-200" />
                      <div className="text-sm font-medium text-primary-400">
                        {ticket?.price
                          ? `From: ${currencyFormatter.format(ticket.price)}`
                          : "Check on partners site"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-2 text-primary-400">
                    <Wallet03Icon className="w-4" />
                    <div className="text-sm font-medium">
                      Check on partners site
                    </div>
                  </div>
                )}
              </div>
            </div>

            <VerifyUserInfo
              event={eventData}
              preferences={preferences}
              user={user}
              onJoinTribe={() => setIsJoinModalOpen(true)}
            />

            <Dialog.Root
              open={isJoinModalOpen}
              onOpenChange={setIsJoinModalOpen}
            >
              <Dialog.Portal>
                <Dialog.Overlay
                  className="fixed inset-0 bg-black/50"
                  style={overlayStyles}
                />
                <Dialog.Content
                  className="bg-[#24242d] z-[999]"
                  style={contentStyles}
                >
                  <div className="relative">
                    <Dialog.Title className="text-xl font-semibold mb-2 text-primary-100">
                      Join {eventData.event_name}
                    </Dialog.Title>
                    <div className="pt-2 pb-4 text-center">
                      <span className="font-bold text-2xl text-utility-pink">
                        {numberToTribeName(
                          availableTribe
                            ? availableTribe.name
                            : `Tribe ${
                                tribeData?.docs?.length
                                  ? tribeData.docs.length + 1
                                  : 1
                              }`
                        )}
                      </span>
                    </div>
                    <JoinTribeCard
                      onJoin={() =>
                        joinTribeMutation({
                          event_id: eventData._id,
                        })
                      }
                      pending={isPending}
                      eventImage={eventData.banner_image}
                      tribe={availableTribe ?? ({} as Tribe)}
                    />

                    <Dialog.Close asChild>
                      <button
                        className="absolute top-0 right-0 inline-flex items-center justify-center rounded-full w-8 h-8 hover:bg-primary-800 text-primary-400"
                        aria-label="Close"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </Dialog.Close>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>

            <div className="text-primary-400 text-sm mt-6 leading-[150%]">
              {splitTextIntoSections(eventData.about)}
            </div>

            {eventData.event_spotify_playlist && (
              <div className="flex flex-wrap items-center gap-2 mt-6">
                <a
                  href={eventData.event_spotify_playlist}
                  className="text-[#1DB954] bg-primary-900 font-medium inline-flex items-center gap-2 py-3 px-4 rounded-md"
                >
                  <SpotifyIcon />
                  <div className="text-primary-100">
                    Get the Spotify Playlist
                  </div>
                </a>
                <a
                  href={eventData.event_spotify_playlist}
                  className="bg-primary-900 shadow-primary-800 font-medium inline-flex items-center gap-2 py-3 px-4 rounded-md"
                  style={{
                    boxShadow: `0px 53px 15px 0px rgba(0, 0, 0, 0.00), 0px 34px 14px 0px rgba(0, 0, 0, 0.01), 0px 19px 11px 0px rgba(0, 0, 0, 0.05), 0px 8px 8px 0px rgba(0, 0, 0, 0.09), 0px 2px 5px 0px rgba(0, 0, 0, 0.10)`,
                  }}
                >
                  <AppleIcon className="text-[#A2AAAD]" />
                  <div className="text-primary-100">
                    Get the Apple Music Playlist
                  </div>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog.Root open={isRedirectingModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            className="fixed inset-0 bg-black/50"
            style={overlayStyles}
          />
          <Dialog.Content className="bg-[#24242d]" style={contentStyles}>
            <div className="flex flex-col items-center justify-center gap-4 p-6">
              <Loader2 className="h-8 w-8 animate-spin text-primary-100" />
              <Dialog.Title className="text-xl font-semibold text-primary-100">
                Opening Ticket Page
              </Dialog.Title>
              <Dialog.Description className="text-center text-primary-400">
                We're redirecting you to the ticket provider. Hang tight!
              </Dialog.Description>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

export default Event;
